/**
 * Pure derivations over (session, config) for the matching setup flow.
 * Ported from the prototype's `renderVals`. No React here, so it runs under
 * the repo's esbuild + node test pattern.
 */
import type {
  Coach,
  Column,
  DoNotMatchGroup,
  DoNotMatchPair,
  MatchingConfig,
  Participant,
  ParticipantId,
  SlotId,
} from './types';

/** "6" when equal, otherwise "6 to 8". */
export function rangeLabel(a: number, b: number): string {
  return a === b ? String(a) : `${a} to ${b}`;
}

/** "A", "A and B", "A, B and C". */
export function listLabel(values: string[]): string {
  if (values.length < 2) return values.join('');
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')} and ${values[values.length - 1]}`;
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * A slot's display name. SGOS ids carry the cohort code ("GS26-TS1"), which
 * every slot in a session shares, so only the "TS1" part is shown.
 */
export function slotLabel(slot: SlotId): string {
  return /(?:^|-)(TS\d+)$/i.exec(slot)?.[1] ?? slot;
}

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------

export interface GroupsRange {
  /** Fewest groups: everyone in groups of the largest size. */
  lo: number;
  /** Most groups: everyone in groups of the smallest size. */
  hi: number;
  label: string;
}

export function groupsRange(
  people: number,
  size: { min: number; max: number }
): GroupsRange {
  const lo = Math.ceil(people / Math.max(size.max, 1));
  const hi = Math.floor(people / Math.max(size.min, 1));
  return { lo, hi, label: rangeLabel(lo, hi) };
}

/** Can `count` people be split into whole groups within the size range? */
export function splitsIntoGroups(
  count: number,
  size: { min: number; max: number }
): boolean {
  return Math.ceil(count / size.max) <= Math.floor(count / size.min);
}

// ---------------------------------------------------------------------------
// Participants
// ---------------------------------------------------------------------------

export function effectiveSlots(
  p: Participant,
  config: MatchingConfig
): SlotId[] {
  return config.participants.availability[p.id] ?? p.sourceSlots;
}

export function isAvailabilityAdjusted(
  p: Participant,
  config: MatchingConfig
): boolean {
  const o = config.participants.availability[p.id];
  if (!o) return false;
  const a = new Set(o);
  return (
    o.length !== p.sourceSlots.length || p.sourceSlots.some(s => !a.has(s))
  );
}

export function adjustedCount(
  participants: Participant[],
  config: MatchingConfig
): number {
  return participants.filter(p => isAvailabilityAdjusted(p, config)).length;
}

// ---------------------------------------------------------------------------
// One-to-one do-not-match (reflexive pairs)
// ---------------------------------------------------------------------------

export function pairOf(a: ParticipantId, b: ParticipantId): DoNotMatchPair {
  return a < b ? [a, b] : [b, a];
}

export function pairKey(a: ParticipantId, b: ParticipantId): string {
  return pairOf(a, b).join(',');
}

/** Every pair in the session data: a listed b, or b listed a. */
export function sourcePairKeys(participants: Participant[]): Set<string> {
  const keys = new Set<string>();
  for (const p of participants)
    for (const other of p.sourceDoNotMatch) keys.add(pairKey(p.id, other));
  return keys;
}

/** The pairs matching will use: session data, plus added, minus removed. */
export function doNotMatchPairKeys(
  participants: Participant[],
  config: MatchingConfig
): Set<string> {
  const keys = sourcePairKeys(participants);
  for (const [a, b] of config.participants.doNotMatch.added)
    keys.add(pairKey(a, b));
  for (const [a, b] of config.participants.doNotMatch.removed)
    keys.delete(pairKey(a, b));
  return keys;
}

/** For each participant, everyone they're kept apart from, in both directions. */
export function doNotMatchIndex(
  participants: Participant[],
  config: MatchingConfig
): Map<ParticipantId, ParticipantId[]> {
  const index = new Map<ParticipantId, ParticipantId[]>(
    participants.map(p => [p.id, []])
  );
  for (const key of doNotMatchPairKeys(participants, config)) {
    const [a, b] = key.split(',');
    index.get(a)?.push(b);
    index.get(b)?.push(a);
  }
  return index;
}

/** Number of one-to-one pairs (each pair counted once). */
export function oneToOneDoNotMatchCount(
  participants: Participant[],
  config: MatchingConfig
): number {
  return doNotMatchPairKeys(participants, config).size;
}

/** Emails this person listed that match no participant and haven't been removed. */
export function unrecognizedDoNotMatch(
  p: Participant,
  config: MatchingConfig
): string[] {
  const removed = new Set(
    config.participants.doNotMatch.removedUnrecognized[p.id] ?? []
  );
  return p.sourceUnrecognizedDoNotMatch.filter(e => !removed.has(e));
}

export function unrecognizedDoNotMatchCount(
  participants: Participant[],
  config: MatchingConfig
): number {
  return participants.reduce(
    (n, p) => n + unrecognizedDoNotMatch(p, config).length,
    0
  );
}

/** How many participants can make each slot, in slot order. */
export function participantSlotCounts(
  participants: Participant[],
  slots: SlotId[],
  config: MatchingConfig
): number[] {
  return slots.map(
    s => participants.filter(p => effectiveSlots(p, config).includes(s)).length
  );
}

/** Case-insensitive participant search by name or email. */
export function searchParticipants(
  participants: Participant[],
  query: string
): Participant[] {
  const q = query.trim().toLowerCase();
  if (!q) return participants;
  return participants.filter(
    p => p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q)
  );
}

// ---------------------------------------------------------------------------
// Column values
// ---------------------------------------------------------------------------

export interface ValueCount {
  value: string;
  count: number;
}

/**
 * Each value of a column with how many participants have it. Multi-choice
 * columns count a person once per value. Numbers sort numerically; everything
 * else sorts by count, largest first.
 */
export function valueCounts(
  participants: Participant[],
  column: Column
): ValueCount[] {
  const counts = new Map<string, number>();
  for (const p of participants) {
    const v = p.attributes[column.key];
    if (v === null || v === undefined) continue;
    for (const x of Array.isArray(v) ? v : [v])
      counts.set(x, (counts.get(x) ?? 0) + 1);
  }
  const out = [...counts.entries()].map(([value, count]) => ({ value, count }));
  if (column.kind === 'number')
    return out.sort((a, b) => Number(a.value) - Number(b.value));
  return out.sort(
    (a, b) => b.count - a.count || a.value.localeCompare(b.value)
  );
}

// ---------------------------------------------------------------------------
// Do-not-match groups
// ---------------------------------------------------------------------------

export function parseEmails(text: string): string[] {
  return text
    .split(/[,;\n]+/)
    .map(x => x.trim())
    .filter(x => x.length > 0);
}

export interface DoNotMatchResolution {
  totalEmails: number;
  notFound: { email: string; list: string }[];
  /** For each participant, the others they share a list with. */
  pairs: Record<ParticipantId, ParticipantId[]>;
}

export function resolveDoNotMatchGroups(
  groups: DoNotMatchGroup[],
  participants: Participant[]
): DoNotMatchResolution {
  const byEmail = new Map(
    participants.filter(p => p.email).map(p => [p.email.toLowerCase(), p.id])
  );
  const pairs: Record<ParticipantId, ParticipantId[]> = {};
  const notFound: DoNotMatchResolution['notFound'] = [];
  let totalEmails = 0;
  for (const g of groups) {
    totalEmails += g.emails.length;
    const found: ParticipantId[] = [];
    for (const e of g.emails) {
      const id = byEmail.get(e.toLowerCase());
      if (id) found.push(id);
      else notFound.push({ email: e, list: g.name });
    }
    for (const a of found) {
      const set = new Set(pairs[a] ?? []);
      for (const b of found) if (b !== a) set.add(b);
      pairs[a] = [...set];
    }
  }
  return { totalEmails, notFound, pairs };
}

// ---------------------------------------------------------------------------
// Coaches
// ---------------------------------------------------------------------------

export function coachSlots(c: Coach, config: MatchingConfig): SlotId[] {
  return config.coaches[c.id]?.availableSlots ?? c.sourceSlots;
}

/**
 * What the coach originally said about a slot. These stay visible after an
 * admin toggles the switch, so the original answer is never lost.
 */
export interface CoachSlotMarkers {
  /** The coach answered "partial" for this slot. */
  partial: boolean;
  /** The coach answered "unavailable" for this slot. */
  unavailable: boolean;
  /** The coach wasn't asked about this slot. */
  notRequested: boolean;
}

/** What to flag on a coach's slot, from their availability request and response. */
export function coachSlotMarkers(c: Coach, slot: SlotId): CoachSlotMarkers {
  return {
    partial: c.slotResponses[slot] === 'partial',
    unavailable: c.slotResponses[slot] === 'unavailable',
    notRequested: c.requestedSlots !== null && !c.requestedSlots.includes(slot),
  };
}

const REQUEST_STATUS_LABELS: Record<string, string> = {
  pending: 'No response',
  declined: 'Declined',
  expired: 'Expired',
  cancelled: 'Cancelled',
  superseded: 'Superseded',
};

/** A badge for a coach whose availability request isn't confirmed, or `null`. */
export function coachRequestLabel(c: Coach): string | null {
  if (!c.requestStatus || c.requestStatus === 'confirmed') return null;
  return REQUEST_STATUS_LABELS[c.requestStatus] ?? c.requestStatus;
}

/** The coach's cap as set (`null` = no cap). A config entry wins even when it is `null`. */
export function coachMaxGroups(
  c: Coach,
  config: MatchingConfig
): number | null {
  const entry = config.coaches[c.id];
  return entry ? entry.maxGroups : c.sourceMaxGroups;
}

/**
 * The most groups the coach can actually run. A coach runs at most one group
 * per available slot, which is also what "no cap" means to matching-server.
 */
export function coachEffectiveGroups(c: Coach, config: MatchingConfig): number {
  const slots = coachSlots(c, config).length;
  const max = coachMaxGroups(c, config);
  return max === null ? slots : Math.min(max, slots);
}

/** How many coaches are available at each slot, in slot order. */
export function coachSlotCounts(
  coaches: Coach[],
  slots: SlotId[],
  config: MatchingConfig
): number[] {
  return slots.map(
    s => coaches.filter(c => coachSlots(c, config).includes(s)).length
  );
}

export function coachCapacityTotal(
  coaches: Coach[],
  config: MatchingConfig
): number {
  return coaches.reduce((n, c) => n + coachEffectiveGroups(c, config), 0);
}

export type CapacityLevel = 'ok' | 'warn' | 'bad';

export interface CapacityStatus {
  level: CapacityLevel;
  total: number;
  message: string;
  zeroSlots: SlotId[];
}

export function coachCapacityStatus(
  coaches: Coach[],
  slots: SlotId[],
  config: MatchingConfig,
  participantCount: number
): CapacityStatus {
  const total = coachCapacityTotal(coaches, config);
  const groups = groupsRange(participantCount, config.groupSize);
  const counts = coachSlotCounts(coaches, slots, config);
  const zeroSlots = slots.filter((_, i) => counts[i] === 0);
  if (total < groups.lo) {
    return {
      level: 'bad',
      total,
      zeroSlots,
      message: `Not enough capacity. Your group size requirement needs at least ${groups.lo} groups. Coaches cover ${total}.`,
    };
  }
  if (zeroSlots.length > 0) {
    return {
      level: 'warn',
      total,
      zeroSlots,
      message: `No coaches are available ${listLabel(zeroSlots.map(slotLabel))}.`,
    };
  }
  if (total < groups.hi) {
    return {
      level: 'warn',
      total,
      zeroSlots,
      message: `Coaches cover ${total} groups. Your group size requirement could need up to ${groups.hi}.`,
    };
  }
  return {
    level: 'ok',
    total,
    zeroSlots,
    message: `Coaches cover ${total} groups. Your group size requirement needs ${groups.label}.`,
  };
}
